/**
 * Invoice Trigger — form to fire POST /billing/invoices/generate (Phase 8.5).
 *
 * Supports: period picker (YYYY-MM), optional tenant selector, dry_run toggle.
 * Displays the invoice results inline (line items per tenant + Stripe invoice ID).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { billingService } from "@re-platform/api-client";
import type { InvoiceResult } from "@re-platform/api-client";
import { api } from "../../lib/api";
import {
  bodyRow,
  tableShell,
  TableScroll,
  Td,
  Th,
  theadRow,
} from "../../lib/tables";

function fmt(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function defaultPeriod(): string {
  const d = new Date();
  const m = d.getMonth(); // 0-indexed
  const y = m === 0 ? d.getFullYear() - 1 : d.getFullYear();
  const mn = m === 0 ? 12 : m;
  return `${y}-${String(mn).padStart(2, "0")}`;
}

interface TenantOption {
  id: string;
  display_name: string;
}

export function InvoiceTrigger() {
  const [period, setPeriod] = useState(defaultPeriod);
  const [tenantId, setTenantId] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<InvoiceResult[] | null>(null);
  const [resultMeta, setResultMeta] = useState<{
    period_start: string;
    period_end: string;
    dry_run: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantsQ = useQuery<TenantOption[]>({
    queryKey: ["tenants", "slim"],
    queryFn: async () => {
      const rows =
        await api.request<{ id: string; display_name: string }[]>(
          "/admin/tenants",
        );
      return rows;
    },
    staleTime: 60_000,
  });

  async function submit() {
    setRunning(true);
    setError(null);
    setResults(null);
    try {
      const resp = await billingService.generateInvoices(api, {
        period: period || undefined,
        tenant_id: tenantId || undefined,
        dry_run: dryRun,
      });
      setResults(resp.invoices);
      setResultMeta({
        period_start: resp.period_start,
        period_end: resp.period_end,
        dry_run: resp.dry_run,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Generate invoices</h1>

      <div
        style={{
          border: "1px solid var(--rp-color-border)",
          borderRadius: "var(--rp-radius-md, 6px)",
          padding: 20,
          maxWidth: 500,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 13,
          }}
        >
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Billing period (YYYY-MM)
          </span>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              padding: "6px 8px",
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-sm)",
              fontSize: 13,
              width: 200,
            }}
          />
          <span style={{ fontSize: 11, color: "var(--rp-color-text-muted)" }}>
            Leave empty to default to prior calendar month.
          </span>
        </label>

        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 13,
          }}
        >
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            Tenant (optional — leave blank to run all)
          </span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            style={{
              padding: "6px 8px",
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-sm)",
              fontSize: 13,
              width: 260,
            }}
          >
            <option value="">All active tenants</option>
            {(tenantsQ.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name}
              </option>
            ))}
          </select>
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          <span>
            Dry run{" "}
            <span style={{ color: "var(--rp-color-text-muted)" }}>
              — calculate totals but do not create Stripe invoices
            </span>
          </span>
        </label>

        {!dryRun && (
          <div
            style={{
              background: "var(--rp-color-warning-faint)",
              border: "1px solid var(--rp-color-warning)",
              borderRadius: "var(--rp-radius-sm)",
              padding: "8px 12px",
              fontSize: 12,
              color: "var(--rp-color-warning-dark)",
            }}
          >
            ⚠ Live mode — Stripe invoices will be created and emailed to
            customers.
          </div>
        )}

        <div>
          <button
            type="button"
            className="rp-btn rp-btn--primary"
            disabled={running}
            onClick={submit}
          >
            {running
              ? "Running…"
              : dryRun
                ? "Preview invoices"
                : "Generate invoices"}
          </button>
        </div>
      </div>

      {error && (
        <p style={{ color: "var(--rp-color-danger)", marginTop: 16 }}>
          {error}
        </p>
      )}

      {results !== null && resultMeta && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>
            Results
            {resultMeta.dry_run && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  fontWeight: 400,
                  padding: "2px 8px",
                  background: "var(--rp-color-warning-faint)",
                  borderRadius: 99,
                  color: "var(--rp-color-warning-dark)",
                }}
              >
                DRY RUN
              </span>
            )}
          </h2>
          <p
            style={{
              fontSize: 13,
              color: "var(--rp-color-text-muted)",
              marginBottom: 16,
            }}
          >
            Period: {resultMeta.period_start} → {resultMeta.period_end} ·{" "}
            {results.length} invoice
            {results.length !== 1 ? "s" : ""}
          </p>

          {results.length === 0 && (
            <p style={{ color: "var(--rp-color-text-muted)" }}>
              No active billing plans found for the selected scope.
            </p>
          )}

          {results.map((inv) => (
            <InvoiceCard key={inv.tenant_id} inv={inv} />
          ))}
        </div>
      )}
    </>
  );
}

function InvoiceCard({ inv }: { inv: InvoiceResult }) {
  return (
    <div
      style={{
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-md, 6px)",
        padding: 16,
        marginBottom: 12,
        maxWidth: 600,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            Tenant <code style={{ fontSize: 12 }}>{inv.tenant_id}</code>
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--rp-color-text-muted)",
              marginTop: 2,
            }}
          >
            Plan: {inv.plan_type}
            {inv.stripe_invoice_id && (
              <>
                {" · "}
                Stripe:{" "}
                <code style={{ fontSize: 11 }}>{inv.stripe_invoice_id}</code>
              </>
            )}
          </div>
          {inv.already_billed && (
            <span
              style={{
                display: "inline-block",
                marginTop: 6,
                fontSize: 11,
                padding: "2px 8px",
                background: "var(--rp-color-info-faint)",
                borderRadius: 99,
                color: "var(--rp-color-info-dark)",
              }}
            >
              ALREADY BILLED — skipped (idempotent)
            </span>
          )}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>
          {fmt(inv.amount_usd)}
        </div>
      </div>

      {inv.line_items.length > 0 && (
        <TableScroll style={{ marginTop: 12 }}>
          <table style={tableShell}>
            <thead>
              <tr style={theadRow}>
                <Th>Line item</Th>
                <Th numeric>Amount</Th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, i) => (
                <tr key={i} style={bodyRow}>
                  <Td>{li.description}</Td>
                  <Td numeric>{fmt(li.amount_usd)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </div>
  );
}
